import { useEffect, useRef, useMemo } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar }              from '@/components/layout/Sidebar'
import { MobileHeader }          from '@/components/layout/MobileHeader'
import { BottomNav }             from '@/components/layout/BottomNav'
import { FooterActionBar }       from '@/components/layout/FooterActionBar'
import { useFooterActionsStore } from '@/store/footer-actions-store'
import { useMobileHeaderStore }  from '@/store/mobile-header-store'
import { ROUTES }                from '@/lib/routes'
import { SignalRProvider }       from '@/providers/SignalRProvider'
import { MobileSidebarProvider } from '@/providers/MobileSidebarProvider'
import { SessionGuard }          from '@/auth/SessionGuard'
import { InactivityWatcher }     from '@/auth/InactivityWatcher'

const NAV_ROOT_PATHS = new Set(ROUTES.filter(r => r.inBottomNav).map(r => r.path))

export function AppLayout() {
  const mainRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  // Reserva espacio extra abajo en mobile solo cuando alguna vista registró
  // botones vía <StickyActionBar> — así páginas sin footer no arrastran
  // padding vacío. En rutas hijas (navbar oculta) no se reserva espacio
  // para el BottomNav.
  const hasFooterActions = useFooterActionsStore(s => !!s.content)
  const isRootNav = useMemo(() => NAV_ROOT_PATHS.has(pathname), [pathname])
  // Una ruta root puede ocultar igual la navbar (ej. send-money registra
  // hideBottomNav vía useSetMobileHeader). El BottomNav ya respetaba ese flag
  // pero el layout no: el footer de acciones quedaba levantado `bottom-16`
  // (el alto de una navbar que no se dibuja) y el botón de avanzar flotaba
  // por encima del pie. La navbar se considera visible solo si se dibuja.
  const hideBottomNav = useMobileHeaderStore(s => s.hideBottomNav)
  const navVisible    = isRootNav && !hideBottomNav

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 })
  }, [pathname])

  return (
    <SignalRProvider>
        <MobileSidebarProvider>
          <InactivityWatcher />
          <div className="flex h-screen overflow-hidden bg-[var(--color-background)]">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
              <MobileHeader />
              <main ref={mainRef} className={`flex-1 overflow-y-auto lg:pb-0 ${hasFooterActions ? (navVisible ? 'pb-44' : 'pb-36') : navVisible ? 'pb-16' : 'pb-0'}`}>
                <SessionGuard>
                  {/* Single source of truth for content width.
                      700px max on desktop, full-width on mobile. */}
                  <div className="min-h-full flex flex-col w-full lg:max-w-[700px] lg:mx-auto">
                    <Outlet />
                    <FooterActionBar navVisible={navVisible} />
                  </div>
                </SessionGuard>
              </main>
            </div>
            <BottomNav />
          </div>
        </MobileSidebarProvider>
    </SignalRProvider>
  )
}
